import { Check, ChevronDown, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/lib/utils';

type ScoringExamplesProps = {
  score1: string;
  score3: string;
  score5: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onUpdate?: (examples: { score1: string; score3: string; score5: string }) => void;
};

export function ScoringExamplesPanel({
  score1,
  score3,
  score5,
  defaultOpen,
  onOpenChange,
  onUpdate
}: ScoringExamplesProps): React.ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({ score1, score3, score5 });

  function startEdit(): void {
    setEditValues({ score1, score3, score5 });
    setEditing(true);
    setOpen(true);
  }

  function saveEdit(): void {
    onUpdate?.(editValues);
    setEditing(false);
  }

  function cancelEdit(): void {
    setEditValues({ score1, score3, score5 });
    setEditing(false);
  }

  const scores = [
    { key: 'score1' as const, label: t('wizard.questions.score1'), color: 'text-destructive' },
    { key: 'score3' as const, label: t('wizard.questions.score3'), color: 'text-muted-foreground' },
    { key: 'score5' as const, label: t('wizard.questions.score5'), color: 'text-green-600' }
  ];

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            const next = !open;
            setOpen(next);
            onOpenChange?.(next);
          }}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} />
          {t('wizard.questions.scoringExamples')}
        </button>
        {open && onUpdate && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Pencil className="size-3" />
            {t('wizard.questions.editScoringExamples')}
          </button>
        )}
        {editing && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon-sm" onClick={saveEdit}>
              <Check className="size-3 text-primary" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={cancelEdit}>
              <X className="size-3 text-muted-foreground" />
            </Button>
          </div>
        )}
      </div>

      {open && (
        <div className="mt-2 flex flex-col gap-2 sm:grid sm:grid-cols-3">
          {scores.map(({ key, label, color }) => (
            <div key={key} className="flex flex-col gap-0.5 sm:gap-1">
              <p className={cn('text-xs font-medium', color)}>{label}</p>
              {editing ? (
                <Textarea
                  value={editValues[key]}
                  onChange={(e) => {
                    setEditValues((prev) => ({ ...prev, [key]: e.target.value }));
                  }}
                  className="min-h-[60px] text-xs"
                />
              ) : (
                <p className="text-xs text-foreground">{{ score1, score3, score5 }[key]}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
